import { getPostAuthUserInfo } from 'auth/auth-helpers/auth-helper';
import { getGatewayGrantToken } from 'auth/auth-helpers/gateway-grant';
import { AuthStore } from 'auth/storage/auth-storage-manager';
import {
  AuthenticationMethod,
  CreateIbmGrantAuthMethodProps
} from 'auth/types/authentication-method.type';
import { TokenResponse } from 'auth/types/token-response.type';
import { differenceInSeconds } from 'date-fns';
import { getAccess } from 'auth/auth-helpers/get-access';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';

const logout =
  ({ setSignedInUser, env, navigate }: CreateIbmGrantAuthMethodProps) =>
  (): Promise<void> => {
    AuthStore.setUserInfo(undefined);
    setSignedInUser(undefined);

    const redirect =
      env.auth.logoutRedirects.find((redirects) => document.referrer.includes(redirects.source)) ??
      env.auth.logoutRedirects.find((redirects) => redirects.default);

    navigate(redirect!.redirectTo);
    return Promise.resolve();
  };

const login = (props: CreateIbmGrantAuthMethodProps) => async (): Promise<boolean> => {
  const { setSignedInUser, setToken, env } = props;

  const token = await getGatewayGrantToken(
    env.auth.ibmGrant.tokenProvider,
    env.auth.ibmGrant.clientId,
    env.auth.ibmGrant.webSealRedirect,
    env.auth.ibmGrant.scopes,
    env.auth.ibmGrant.realm
  );

  if (!token) return false;

  const userInfo = await getPostAuthUserInfo(
    token.access_token,
    env.auth.ibmGrant.userInfoProvider
  );

  if (!userInfo) return false;

  const getAccessSuccess = await getUserAccess(props)(token);
  if (!getAccessSuccess) return false;

  AuthStore.setAuthToken(token);
  AuthStore.setUserInfo(userInfo);
  setSignedInUser(userInfo);
  setToken(token);
  return true;
};

const getUserAccess =
  (props: CreateIbmGrantAuthMethodProps) =>
  async (token: TokenResponse | undefined): Promise<boolean> => {
    const getAccessSuccess = await getAccess({
      getSignedInUser: props.getSignedInUser,
      setSignedInUser: props.setSignedInUser,
      setCurrentAuthenticatedUserWithFeatures: props.setCurrentAuthenticatedUserWithFeatures,
      env: props.env,
      navigate: props.navigate
    })(token);
    if (!getAccessSuccess) {
      const logoutUser = logout(props);
      await logoutUser();
    }
    return getAccessSuccess;
  };

const refreshSession = (props: CreateIbmGrantAuthMethodProps) => async (): Promise<boolean> => {
  const { setSignedInUser, setToken, env } = props;
  const currentToken = AuthStore.getAuthToken();
  const logoutUser = logout(props);
  if (!currentToken) {
    await logoutUser();
    return false;
  }
  const secondsUntilExpiry = differenceInSeconds(currentToken.token_expiry_time, new Date());
  const isTokenExpired = secondsUntilExpiry < env.auth.refreshIntervalSeconds;
  if (!isTokenExpired) return true;

  const onRefreshSuccess = async (refreshedToken: TokenResponse): Promise<boolean> => {
    const tokenExpiryTime = new Date();
    tokenExpiryTime.setSeconds(tokenExpiryTime.getSeconds() + refreshedToken.expires_in);
    refreshedToken.token_expiry_time = tokenExpiryTime;

    const userInfo = await getPostAuthUserInfo(
      refreshedToken.access_token,
      replaceWithDynamicHost(authConfig.ibmGrant.userInfoProvider, env.apiHostMappings)
    );

    if (!userInfo) {
      await logoutUser();
      return false;
    }

    AuthStore.setAuthToken(refreshedToken);
    AuthStore.setUserInfo(userInfo);
    setToken(refreshedToken);
    setSignedInUser(userInfo);

    return true;
  };

  const authConfig = env.auth;

  // No refresh_token available, so replacing with a request for a new token
  const newToken = await getGatewayGrantToken(
    replaceWithDynamicHost(env.auth.ibmGrant.tokenProvider, env.apiHostMappings),
    env.auth.ibmGrant.clientId,
    replaceWithDynamicHost(env.auth.ibmGrant.webSealRedirect, env.apiHostMappings),
    env.auth.ibmGrant.scopes,
    env.auth.ibmGrant.realm
  );
  if (newToken === undefined) {
    await logoutUser();
    return false;
  }

  return await onRefreshSuccess(newToken);
};

const retrieveUserInfo = () => async (): Promise<boolean> => {
  return Promise.resolve(true);
};

export const createIbmGrantAuthMethod = (
  props: CreateIbmGrantAuthMethodProps
): AuthenticationMethod => {
  return {
    logout: logout(props),
    login: login(props),
    retrieveUserInfo: retrieveUserInfo(),
    getAccess: getUserAccess(props),
    refreshSessionIfExpired: refreshSession(props),
    getSignedInUser: props.getSignedInUser,
    identificationState: props.identificationState
  };
};
